/**
 * Core GA4 module
 *
 * Handles dynamic script injection, page view tracking, and event dispatch.
 * - Reads VITE_GA_MEASUREMENT_ID from env
 * - Only loads in production
 * - Respects Do Not Track (DNT) browser header
 * - Script is injected dynamically after user consent (not in index.html)
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
const IS_PRODUCTION = import.meta.env.PROD;

let isInitialized = false;

/**
 * Check if the browser's Do Not Track setting is enabled.
 */
export function isDNTEnabled(): boolean {
  return navigator.doNotTrack === '1' || (navigator as any).globalPrivacyControl === true;
}

/**
 * Check whether analytics can be loaded:
 * - Must be production build
 * - Must have a measurement ID
 * - Must not have DNT enabled
 */
export function canLoadAnalytics(): boolean {
  return !!IS_PRODUCTION && !!MEASUREMENT_ID && !isDNTEnabled();
}

/**
 * Initialize GA4 by dynamically injecting the gtag.js script.
 * Call this ONLY after the user has granted cookie consent.
 */
export function initGA(): void {
  if (isInitialized || !canLoadAnalytics() || !MEASUREMENT_ID) return;

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', MEASUREMENT_ID, {
    send_page_view: false, // We handle page views manually via usePageTracking
  });

  // Inject the gtag.js script tag
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  isInitialized = true;
}

/**
 * Tear down GA (for when user revokes consent).
 */
export function teardownGA(): void {
  if (!isInitialized) return;

  // Remove the gtag script element
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    'script[src*="googletagmanager.com/gtag"]',
  );
  scripts.forEach((s) => s.remove());

  // Clear dataLayer
  window.dataLayer = [];
  isInitialized = false;
}

/**
 * Track a page view. Called by usePageTracking on route changes.
 */
export function trackPageView(path: string, title?: string): void {
  if (!isInitialized) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title ?? document.title,
  });
}

/**
 * Send a custom GA4 event.
 * All typed event helpers in events.ts call through this.
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>,
): void {
  if (!isInitialized) return;
  window.gtag('event', eventName, params);
}

/**
 * Returns true if GA has been successfully initialized.
 */
export function isGAInitialized(): boolean {
  return isInitialized;
}
